import { useEffect, useRef } from 'react';

/**
 * Debounced autosave.
 *
 * Calls `onSave(value)` once `delayMs` has elapsed without `value`
 * changing. Cancels in-flight timers on unmount or new changes.
 *
 * The hook does NOT compare values — every change to the `value`
 * reference resets the timer. Pass a stable reference (e.g. via
 * useMemo) if you don't want object-identity churn to trigger saves.
 */
export function useDebouncedAutosave<T>(
  value: T,
  onSave: (value: T) => void | Promise<void>,
  delayMs = 600,
  enabled = true,
): void {
  // Hold the latest onSave in a ref so the timer always calls the
  // most recent closure without resetting on every render.
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (!enabled) return;
    const handle = setTimeout(() => {
      void onSaveRef.current(value);
    }, delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs, enabled]);
}
